import React, { useCallback, useEffect, useRef, useState } from 'react'

import { FieldData } from 'rc-field-form/lib/interface'
import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { IconSelectButtonGroup, IconSelectOption } from '@atoms/buttons/IconSelectButtonGroup'
import { IconEnum } from '@atoms/images/Icon'
import { Button, Paragraph, Spacer } from '@atoms/index'
import { ResponsivePXValue, theme } from '@client/components/Theme'
import { useConfig } from '@client/contexts/ConfigProvider'
import { SiteHelper } from '@client/lib/SiteHelper'
import { useEvents } from '@contexts/GTMProvider'
import {
  useAddLocationMutation, useAddUserAddressMutation, useGetAppQuery, useRequestAddressNotificationMutation,
  useUpdateLocationMutation, useUserCartQuery, useUserDetailsQuery,
} from '@hooks/api'
import { useProductGroups } from '@hooks/UseProductGroups'
import { useScrollIntoViewOnFocus } from '@hooks/UseScrollIntoViewOnFocus'
import { useScrollToTopOnMount } from '@hooks/UseScrollToTopOnMount'
import { Form, TextInput, TextAreaInput, useForm, CheckBox } from '@molecules/index'
import { PlaceIdLookup } from '@organisms/index'
import { BuildingTypeEnum, DeviceTypeEnum, UpdateLocationInput } from '@uctypes/api/globalTypes'

import { Coordinates } from '../misc/DraggableMap'
import type { PlaceIdOption } from '../misc/PlaceIdLookup'
import { GenericMessageModal } from '../modals/GenericMessageModal'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;

  ${ResponsivePXValue('margin', { desktop: '0 16px' })}

  .submit-button {
    ${ResponsivePXValue('margin', '16px 0')}
  }
  .input-label {
    font-weight: 700;
  }
  input {
   border-color: ${theme.colors.whites.silver};
  }
`

const ActionContainer = styled.div<{ $stickyBottom?: boolean, $variant?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props) => props.$stickyBottom && `
    position: sticky;
    ${ResponsivePXValue('bottom', '8px')};
  `}

  ${(props) => props.$variant === 'horizontal' && `
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
  `}

  .address-action-button {
    ${ResponsivePXValue('width', { mobile: '100%', tablet: '180px', desktop: '180px' })};
  }
`

export interface AddAddressFunctionProps {
  placeId: string
  placeCoordinates: Coordinates
  buildingTypeEnum: BuildingTypeEnum
  streetName: string
  locationName: string
  streetNumber: string
  alias?: string
  company?: string
  buildingName?: string
  instructions?: string
  isDefault?: string

  unitNumber?: string | null
}

export interface AddAddressFormProps {
  onSuccess: () => void

  onCancel?: () => void
  onError: (message: string) => void

  actionButtonText?: string

  secondaryActionButtonText?: string

  displayActionButtons?: boolean

  disabled?: boolean

  checkoutFlowType?: string
}

interface FormData {
  alias: string
  company: string
  buildingTypeEnum: BuildingTypeEnum | null
  buildingName: string | null
  instructions: string
  isDefault: string
  locationName: string | null
  streetName: string | null
  streetNumber: string | null
  unitNumber?: string | null
}

interface AddAddressFormState {
  loading: boolean
  locationLoading: boolean
  placeId: string | null
  locationId: string | null
  placeCoordinates: Coordinates | null
  locationData: {
    locationName: string | null
    streetName: string | null
    streetNumber: string | null
    types: string[] | null
    unitNumber: string | null
  }
  hasAlias: boolean
  buildingTypeEnum: BuildingTypeEnum | null
  showUnsupportedLocationModal: boolean
  displayActionButtons?: boolean
  resetKey: number
  isDefault: boolean
}

const DEFAULT_STATE: AddAddressFormState = {
  loading: false,
  locationLoading: false,
  placeId: null,
  locationId: null,
  placeCoordinates: null,
  locationData: {
    locationName: null,
    streetName: null,
    streetNumber: null,
    types: null,
    unitNumber: null,
  },
  hasAlias: false,
  showUnsupportedLocationModal: false,
  buildingTypeEnum: BuildingTypeEnum.HOUSE,
  resetKey: 0,
  isDefault: true,
}

export const AddAddressForm = React.memo(function AddAddressForm({ onSuccess, onCancel, onError, actionButtonText = 'SAVE ADDRESS', secondaryActionButtonText = 'CANCEL', displayActionButtons = true, disabled = false, checkoutFlowType }: AddAddressFormProps): JSX.Element {

  const config = useConfig()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const [state, setState] = useState<AddAddressFormState>({ ...DEFAULT_STATE, isDefault: true })
  const stateRef = useRef(state)
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [addUserAddressMutation] = useAddUserAddressMutation()
  const [addLocationMutation] = useAddLocationMutation()
  const [updateLocationMutation] = useUpdateLocationMutation()
  const [requestAddressNotification] = useRequestAddressNotificationMutation()
  const [form] = useForm()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastPlaceIdRef = useRef<string | null>(null)
  const isMobile = appData.app.deviceType === DeviceTypeEnum.MOBILE
  const { addToast } = useToasts()

  const scrollRef = useRef(null)
  useScrollIntoViewOnFocus({ scrollContainer: scrollRef.current })
  useScrollToTopOnMount()

  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const currentCategory: string | null = userCartData?.currentUser?.activeMenu?.subscription?.category?.id || null
  const productGroupData = useProductGroups(currentCategory)

  const event = useEvents()
  const hasTriggeredEvent = useRef(false)

  const userId = userDetailsData?.currentUser?.id
  const userEmail = userDetailsData?.currentUser?.email
  const userAddress = userDetailsData?.currentUser?.addresses[0]

  // Helper to check if all required fields are filled
  function areRequiredFieldsFilled() {
    if (!form) return false
    const requiredFields = [
      'streetName',
      'streetNumber',
      // Building type specific required fields
      ...(state.buildingTypeEnum === BuildingTypeEnum.OFFICE ? ['company'] : []),
      ...(state.buildingTypeEnum === BuildingTypeEnum.APARTMENTS ? ['buildingName'] : []),
      ...(state.buildingTypeEnum === BuildingTypeEnum.COMPLEX ? ['buildingName'] : []),
    ]
    return requiredFields.every(field => {
      const value = form.getFieldValue(field)
      return value !== undefined && value !== null && value !== ''
    })
  }

  const isPrimaryButtonDisabled = state.loading || !state.placeId || !state.buildingTypeEnum || disabled || !areRequiredFieldsFilled()

  useEffect(() => {
    if (!hasTriggeredEvent.current && productGroupData && !userAddress) {
      hasTriggeredEvent.current = true
      event.hasSelectedPlan(productGroupData, userId, userEmail, checkoutFlowType)
    }
  }, [productGroupData])

  const setLoading = useCallback((loading: boolean): void => {
    setState((prevState) =>
      update(prevState, {
        loading: { $set: loading },
      }),
    )
  }, [])

  const setLocationLoading = useCallback((locationLoading: boolean): void => {
    setState((prevState) =>
      update(prevState, {
        locationLoading: { $set: locationLoading },
      }),
    )
  }, [])

  const setBuildingTypeEnum = useCallback((buildingTypeEnum: BuildingTypeEnum): void => {
    setState((prevState) =>
      update(prevState, {
        buildingTypeEnum: { $set: buildingTypeEnum },
      }),
    )
    // Reset streetNumber if not HOUSE
    // if (buildingTypeEnum !== BuildingTypeEnum.HOUSE) {
    //   form.setFieldsValue({ streetNumber: '' })
    // }
  }, [form])

  const setShowUnsupportedLocationModal = useCallback((show: boolean): void => {
    setState((prevState) =>
      update(prevState, {
        showUnsupportedLocationModal: { $set: show },
        // Only reset location fields when closing the modal
        placeId: { $set: show ? prevState.placeId : null },
        locationId: { $set: show ? prevState.locationId : null },
        placeCoordinates: { $set: show ? prevState.placeCoordinates : null },
        locationData: { $set: show ? prevState.locationData : DEFAULT_STATE.locationData },
      }),
    )
    if (show) {
      form.resetFields(['locationName', 'streetName', 'streetNumber'])
    }
  }, [form])

  // Robust helper to update location before form submit
  const handleUpdateLocation = useCallback(async (locationId: string | null, formData: FormData, state: AddAddressFormState) => {
    if (!locationId) throw new Error('No location created. Please select a place first.')
    if ((formData.streetName && formData.streetName !== '') || (formData.streetNumber && formData.streetNumber !== '')) {
      const locationUpdate: UpdateLocationInput = {
        buildingTypeEnum: formData.buildingTypeEnum || state.buildingTypeEnum,
        streetName: formData.streetName,
        streetNumber: formData.streetNumber,
        locationName: formData.locationName,
        unitNumber: formData.unitNumber,
      }
      await updateLocationMutation({
        variables: {
          id: locationId,
          input: locationUpdate,
        },
      })
    }
  }, [updateLocationMutation])

  const _handleSuccess = useCallback(
    async (data: FormData) => {
      // Location already updated before submit
      try {
        const locationId = state.locationId
        if (!locationId) {
          throw new Error('No location created. Please select a place first.')
        }
        const isDefault = data?.isDefault ? (data.isDefault as unknown as string[])[0] === 'true' : false
        await addUserAddressMutation({
          variables: {
            input: {
              alias: data.alias,
              instructions: data.instructions,
              company: data.company,
              location: locationId,
              user: userDetailsData?.currentUser.id,
              isDefault: !!isDefault,
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: SiteHelper.getUserRefetchQueries(),
        })
        event.hasAddedAddressCheckoutStep(productGroupData, userDetailsData?.currentUser?.id, state.showUnsupportedLocationModal, userDetailsData?.currentUser?.email, checkoutFlowType)
        onSuccess()
      } catch (e) {
        onError(e.message)
      } finally {
        setLoading(false)
      }
    },
    [state.locationId, userDetailsData, addUserAddressMutation, onSuccess, onError, setLoading, productGroupData, event, checkoutFlowType],
  )

  const _handleChange = useCallback(
    (changedFields: FieldData[]) => {
      const aliasField = changedFields.find((f) => f.name.toString() === 'alias')
      if (aliasField) {
        setState((prevState) =>
          update(prevState, {
            hasAlias: { $set: !!aliasField.value },
          }),
        )
      }

      const buildingTypeField = changedFields.find((f) => f.name.toString() === 'buildingTypeEnum')
      if (buildingTypeField?.value) {
        setBuildingTypeEnum(buildingTypeField.value as BuildingTypeEnum)
      }

      const isDefaultField = changedFields.find((f) => f.name.toString() === 'isDefault')
      if (isDefaultField) {
        const checked = !!(isDefaultField.value && isDefaultField.value.length > 0 && isDefaultField.value[0])
        setState((prevState) => update(prevState, { isDefault: { $set: checked } }))
      }

      const locationFields = ['locationName', 'streetName', 'streetNumber']
      const updatedLocationData = locationFields.reduce<{ [key: string]: string | null }>((acc, field) => {
        const fieldValue = form.getFieldValue(field)
        if (fieldValue !== undefined) {
          acc[field] = fieldValue
        }
        return acc
      }, {})

      setState((prevState) =>
        update(prevState, {
          locationData: {
            $set: {
              ...prevState.locationData,
              ...updatedLocationData,
            },
          },
        }),
      )
    },
    [form, setBuildingTypeEnum],
  )

  const _handlePlaceSelect = useCallback(
    (placeId: string, option: PlaceIdOption): void => {
      if (!placeId || lastPlaceIdRef.current === placeId) {
        return
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        lastPlaceIdRef.current = placeId
        setLocationLoading(true)
        try {
          const addLocationResponse = await addLocationMutation({
            variables: {
              input: {
                placeId,
                types: option.types,
                locationName: option.title.split(',')[0],
                buildingTypeEnum: state.buildingTypeEnum,
              },
            },
          })

          const location = addLocationResponse.data?.addLocation

          // Determine what streetNumber to set based on buildingTypeEnum
          // const shouldSetStreetNumber = state.buildingTypeEnum === BuildingTypeEnum.HOUSE
          // const streetNumberValue = shouldSetStreetNumber ? location.streetNumber : ''

          if (location) {
            if (!location.isLocationSupported) {
              // Set the location data in state, then open modal in callback
              setState((prevState) => {
                const newState = update(prevState, {
                  placeId: { $set: placeId },
                  locationId: { $set: location.id },
                  placeCoordinates: {
                    $set: {
                      latitude: location.center.latitude,
                      longitude: location.center.longitude,
                    },
                  },
                  locationData: {
                    $set: {
                      locationName: location.locationName,
                      streetName: location.streetName,
                      streetNumber: location.streetNumber,
                      types: location.types as string[],
                    },
                  },
                })
                setTimeout(() => setShowUnsupportedLocationModal(true), 0)
                return newState
              })
            } else {
              setState((prevState) =>
                update(prevState, {
                  placeId: { $set: placeId },
                  locationId: { $set: location.id },
                  placeCoordinates: {
                    $set: {
                      latitude: location.center.latitude,
                      longitude: location.center.longitude,
                    },
                  },
                  locationData: {
                    $set: {
                      locationName: location.locationName,
                      streetName: location.streetName,
                      streetNumber: location.streetNumber,
                      types: location.types as string[],
                    },
                  },
                }),
              )
              form.setFieldsValue({
                locationName: location.locationName,
                streetName: location.streetName,
                streetNumber: location.streetNumber,
              })
            }
          }
        } catch (e) {
          onError(e.message)
        } finally {
          setLocationLoading(false)
        }
      }, 500)
    },
    [addLocationMutation, form, onError, setLocationLoading, setShowUnsupportedLocationModal, state.buildingTypeEnum],
  )

  // Building type options for new design
  const buildingTypeOptions: IconSelectOption[] = [
    { value: BuildingTypeEnum.HOUSE, icon: IconEnum.HOME, label: 'House' },
    { value: BuildingTypeEnum.OFFICE, icon: IconEnum.OFFICE, label: 'Office' },
    { value: BuildingTypeEnum.APARTMENTS, icon: IconEnum.APARTMENTS, label: 'Apartments' },
    { value: BuildingTypeEnum.COMPLEX, icon: IconEnum.COMPLEX, label: 'Complex' },
  ]

  const _handleSecondaryActionButtonClick = useCallback((): void => {
    if (onCancel) {
      if (secondaryActionButtonText.toUpperCase() === 'RESET') {
        form.resetFields()
        setState(prevState => update(prevState, { resetKey: { $set: prevState.resetKey + 1 } }))
      }
      onCancel()
    }
  }, [onCancel, secondaryActionButtonText, form])

  const _handleRequestAddressNotification = async (): Promise<void> => {
    try {
      if (!userDetailsData?.currentUser?.id) throw new Error('User not found')
      // Use the latest state from the ref
      const latestState = stateRef.current
      const locationInput = {
        placeId: latestState.placeId,
        types: latestState.locationData.types,
        locationName: latestState.locationData.locationName,
      }
      const response = await requestAddressNotification({
        variables: {
          input: {
            userId: userDetailsData.currentUser.id,
            locationInput,
            alias: form.getFieldValue('alias') || 'Home',
          },
        },
      })
      if (response?.data?.success === true) {
        addToast('We will let you know once we start delivery to this address.', {
          appearance: 'success',
          autoDismiss: true,
        })
      }
    } catch (e) {
      onError(e.message)
    } finally {
      setShowUnsupportedLocationModal(false)
    }
  }

  const handleSubmitWithAlias = useCallback(async () => {
    if (!state.hasAlias) {
      form.setFieldsValue({ alias: 'Home' })
    }
    const formData = form.getFieldsValue()
    try {
      setLoading(true)
      await handleUpdateLocation(state.locationId, formData, state)
      form.submit()
    } catch (e) {
      onError(e.message)
      setLoading(false)
    }
  }, [state, form, handleUpdateLocation, setLoading, onError])

  useEffect(() => { stateRef.current = state }, [state])

  return (
    <Container ref={scrollRef}>
      <GenericMessageModal
        open={state.showUnsupportedLocationModal}
        title={"Unfortunately, we don't deliver to your area just yet."}
        message='Would you like us to notify you when we do?'
        primaryActionText='Yes, keep me posted'
        secondaryActionText='No thanks'
        maxWidth={isMobile ? '100%' : '500px'}
        minWidth={isMobile ? undefined : '500px'}
        // headerContainerPadding={isMobile ? undefined : '0 16px'}
        onPrimaryAction={_handleRequestAddressNotification}
        onSecondaryAction={() => setShowUnsupportedLocationModal(false)}
        onClose={() => setShowUnsupportedLocationModal(false)} />

      <Form
        form={form}
        onFinish={_handleSuccess}
        onFieldsChange={_handleChange}
        disabled={state.loading || disabled}
        initialValues={{
          isDefault: [true],
        }}
        className='add-address-form'>
        <Spacer universal='16px' />
        {/* Building type icon row */}
        <Paragraph variant='p3' superBold color={theme.colors.slates.ironsideGrey}>Building type</Paragraph>
        <Spacer universal='8px' />
        <IconSelectButtonGroup
          options={buildingTypeOptions}
          selected={state.buildingTypeEnum}
          disabled={state.loading || disabled}
          onChange={val => {
            setBuildingTypeEnum(val as BuildingTypeEnum)
            form.setFieldsValue({ buildingTypeEnum: val })
          }}
        />
        <input type="hidden" name="buildingTypeEnum" value={state.buildingTypeEnum || ''} />
        <Spacer universal='8px' />
        <PlaceIdLookup
          onSelect={_handlePlaceSelect}
          resetKey={state.resetKey}
          disabled={state.loading || disabled}
        />
        <TextInput
          name='alias'
          label='Give your address a name'
          placeholder='The main house'
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
          disabled={disabled}
        />
        <Spacer universal='12px' />
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.OFFICE}>
          <TextInput
            name='unitNumber'
            label='Suite or floor'
            placeholder='Enter suite or floor'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a suite or floor' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='company'
            label='Business name'
            placeholder='Head office'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a business name' }]} />
        </If>
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.APARTMENTS}>
          <TextInput
            name='unitNumber'
            label='Apartment number'
            placeholder='00'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input an apartment number' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='buildingName'
            label='Flat/Apartment name'
            placeholder='Harbour Lights'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a flat/apartment name' }]} />
        </If>
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.COMPLEX}>
          <TextInput
            label='Unit number'
            name='unitNumber'
            placeholder='00'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a unit number' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='buildingName'
            label='Security estate name'
            placeholder='Harbour Lights'
            disabled={state.loading || disabled}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a security estate name' }]} />
        </If>
        <Spacer universal='12px' />
        <TextInput
          name='streetName'
          label='Street name'
          placeholder='Street name'
          rules={[{ required: true, message: 'Please input a street name' }]}
          disabled={state.loading || disabled}
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
        />
        <Spacer universal='12px' />
        <TextInput
          name='streetNumber'
          label='Street number'
          placeholder='00'
          disabled={state.loading || disabled}
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
          rules={[{ required: true, message: 'Please input a street number' }]} />
        <Spacer universal='12px' />
        <TextAreaInput
          label='Delivery note'
          className='instructions'
          name='instructions'
          placeholder='Optional'
          showOptional={false}
          disabled={state.loading || disabled}
        />
        <CheckBox
          color='orange'
          name='isDefault'
          showLabel={false}
          showOptional={false}
          options={[{ title: 'Set as default address', value: true }]}
          value={[state.isDefault]}
          disabled={state.loading || disabled}
        />
        <Spacer universal='24px' />
        <If condition={displayActionButtons}>
          <ActionContainer $stickyBottom $variant={isMobile ? 'vertical' : 'horizontal'}>
            <Button
              className='address-action-button'
              variant={isPrimaryButtonDisabled ? 'secondary' : 'primary'}
              loading={state.loading}
              disabled={isPrimaryButtonDisabled}
              color='black'
              fullWidth={isMobile}
              title={actionButtonText}
              onClick={handleSubmitWithAlias} />
            <If condition={!!onCancel}>
              <Spacer mobile='8px' desktop='16px' variant={isMobile ? 'vertical' : 'horizontal'} />
              <Button
                className='address-action-button'
                variant='secondary'
                color='black'
                onClick={_handleSecondaryActionButtonClick}
                title={secondaryActionButtonText}
                disabled={state.loading || disabled}
              />
            </If>
          </ActionContainer>
        </If>
      </Form>
    </Container>
  )
})
